import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { api, errMsg } from "../api";
import { FormField, PrimaryButton } from "./FormField";
import { useRequireAuth } from "../hooks/useRequireAuth";

const MAX_SPACES = 500;

export const CapacityDialog = ({ total, refresh }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(total ?? ""));
  const [busy, setBusy] = useState(false);
  const requireAuth = useRequireAuth();

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.setCapacity(Number(value));
      toast.success(`Lot capacity set to ${res.total} spaces`);
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o && !requireAuth()) return;
        setOpen(o);
        if (o) setValue(String(total ?? ""));
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="capacity-settings-btn"
          className="h-9 rounded-sm border-[#262626] bg-transparent hover:bg-[#1F1F1F] uppercase tracking-widest text-xs gap-2"
        >
          <Settings2 className="w-4 h-4" />
          Capacity
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-sm" data-testid="capacity-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Lot Capacity</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3]">
            Set how many spaces this lot has.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <FormField
            label="Number Of Spaces"
            type="number"
            min={1}
            max={MAX_SPACES}
            data-testid="capacity-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <p className="text-sm text-[#A3A3A3]">
            Increasing adds new empty spaces. Reducing only works if the removed spaces are free and unassigned.
          </p>
          <PrimaryButton data-testid="capacity-save-btn" disabled={busy || !value} onClick={submit}>
            Save Capacity
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
